import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SmilePlus, Pencil } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useCheckins } from '../../../hooks/useJourney'
import { cn } from '../../../lib/utils'

// Daily mood + energy check-in — a two-tap stepper writing to the same
// recovery_checkins row the Journey check-in fills, so either surface
// marks the other as done. Weekly Insights and the Phoenix Assistant
// already read this table; this card just closes the capture gap.
//
// Flow: one question at a time (mood → energy, auto-advance on tap,
// optimistic so the transition is instant), then a one-line summary
// pill. Tapping the summary reopens both scales for edits; any tap
// saves and collapses again.

const MOOD_FACES = ['😖', '🙁', '😐', '🙂', '😄']
const ENERGY_FACES = ['🪫', '😴', '😌', '💪', '⚡']
const MOOD_WORD_KEYS = [1, 2, 3, 4, 5].map((n) => `phoenixTracker.today.moodWord${n}`)
const ENERGY_WORD_KEYS = [1, 2, 3, 4, 5].map((n) => `phoenixTracker.today.energyWord${n}`)

export default function MoodCard() {
  const { t } = useTranslation()
  const todayIso = useMemo(() => new Date().toLocaleDateString('en-CA'), [])
  const { todayCheckin, loading, tableMissing, upsert } = useCheckins({ since: todayIso })
  const [expanded, setExpanded] = useState(false)
  // Optimistic values — the stepper advances on tap, not on the
  // round-trip. Server state wins once it lands (same values anyway).
  const [localVals, setLocalVals] = useState({})

  if (loading || tableMissing) return null

  const mood = localVals.mood ?? todayCheckin?.mood ?? null
  const energy = localVals.energy ?? todayCheckin?.energy ?? null
  const complete = mood != null && energy != null

  const pick = (field, n) => {
    try { navigator.vibrate?.(10) } catch { /* not supported */ }
    const next = { mood, energy, [field]: n }
    setLocalVals((v) => ({ ...v, [field]: n }))
    if (next.mood != null && next.energy != null) setExpanded(false)
    // Carry urges/notes through — the shared upsert overwrites every
    // column, and this card must not wipe what Journey wrote today.
    upsert({
      mood: next.mood,
      energy: next.energy,
      urges_today: todayCheckin?.urges_today ?? false,
      notes: todayCheckin?.notes ?? '',
    })
  }

  // ── done: one-line summary pill ──────────────────────────────────
  if (complete && !expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} className="w-full text-start">
        <Card className="border-app-primary-500/20 bg-gradient-to-r from-app-primary-500/10 to-transparent">
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-2xl leading-none flex-shrink-0">{MOOD_FACES[mood - 1]}</span>
            <div className="flex-1 min-w-0 flex items-center gap-2 text-xs text-muted-foreground" dir="auto">
              <span className="truncate">
                <span className="font-semibold text-foreground">{t(MOOD_WORD_KEYS[mood - 1])}</span>
                {' · '}
                {ENERGY_FACES[energy - 1]} <span className="font-semibold text-foreground">{t(ENERGY_WORD_KEYS[energy - 1])}</span>
              </span>
            </div>
            <Pencil className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </CardContent>
        </Card>
      </button>
    )
  }

  // ── edit mode: both scales, any tap saves + collapses ────────────
  if (complete && expanded) {
    return (
      <Card>
        <CardContent className="p-3 sm:p-4 space-y-3">
          <Header t={t} title={t('phoenixTracker.today.moodTitle')} />
          <FaceScale faces={MOOD_FACES} wordKeys={MOOD_WORD_KEYS} value={mood} onPick={(n) => pick('mood', n)} t={t} />
          <FaceScale faces={ENERGY_FACES} wordKeys={ENERGY_WORD_KEYS} value={energy} onPick={(n) => pick('energy', n)} t={t} />
        </CardContent>
      </Card>
    )
  }

  // ── stepper: one question at a time ──────────────────────────────
  const step = mood == null ? 'mood' : 'energy'
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <Header
          t={t}
          title={step === 'mood' ? t('phoenixTracker.today.moodTitle') : t('phoenixTracker.today.energyTitle')}
          step={step === 'mood' ? 1 : 2}
        />
        {step === 'mood' ? (
          <FaceScale faces={MOOD_FACES} wordKeys={MOOD_WORD_KEYS} value={mood} onPick={(n) => pick('mood', n)} t={t} />
        ) : (
          <FaceScale faces={ENERGY_FACES} wordKeys={ENERGY_WORD_KEYS} value={energy} onPick={(n) => pick('energy', n)} t={t} />
        )}
      </CardContent>
    </Card>
  )
}

function Header({ t, title, step = null }) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
        <SmilePlus className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
        {title}
      </h2>
      {step != null && (
        <span className="flex items-center gap-1 flex-shrink-0" aria-hidden="true">
          {[1, 2].map((n) => (
            <span
              key={n}
              className={cn(
                'rounded-full transition-all duration-300',
                n === step ? 'w-3.5 h-1.5 bg-app-primary-500' : 'w-1.5 h-1.5 bg-muted-foreground/30',
              )}
            />
          ))}
        </span>
      )}
    </div>
  )
}

// Five big boxless emoji: unselected ones sit dim + desaturated, the
// active one pops up a size with its word lit underneath. Words render
// under every level so the scale reads at a glance.
function FaceScale({ faces, wordKeys, value, onPick, t }) {
  return (
    <div className="grid grid-cols-5">
      {faces.map((face, i) => {
        const n = i + 1
        const active = value === n
        return (
          <button
            key={n}
            type="button"
            onClick={() => onPick(n)}
            aria-pressed={active}
            className="group flex flex-col items-center gap-1.5 py-1 select-none"
          >
            <span
              className={cn(
                'text-[28px] leading-none transition-all duration-200 ease-out group-active:scale-90',
                active ? 'scale-125' : 'opacity-40 saturate-[0.35] group-hover:opacity-70',
              )}
            >
              {face}
            </span>
            <span
              className={cn(
                'text-[10px] leading-none transition-colors duration-200 truncate max-w-full px-0.5',
                active ? 'text-app-primary-500 font-semibold' : 'text-muted-foreground/70',
              )}
              dir="auto"
            >
              {t(wordKeys[i])}
            </span>
          </button>
        )
      })}
    </div>
  )
}
